import * as api from "../client";

const BASE = "/v1/profiles/bulk-website-relabel";

/**
 * Client for the SOFTSUPP-532 bulk website relabel endpoints on profiles-api
 * (proxied by staff-bff). Types mirror `schemas/bulkWebsiteRelabel.ts` on the
 * API — validate is a dry run, apply performs the writes and returns the
 * pre-update state so the UI can build a rollback manifest.
 */

export type RelabelTable = "firm_profiles" | "lawyer_profiles";

/**
 * Valid site editions, mirroring WEBSITE_VALUES on profiles-api. An invalid
 * website in ANY row fails the whole request with a 400, so the client must
 * pre-validate against this list and exclude offending rows before calling.
 */
export const RELABEL_WEBSITES = ["com_en", "com_zh", "fr", "de"] as const;

export type RelabelWebsite = (typeof RELABEL_WEBSITES)[number];

/**
 * Rows per `apply` request. Callers with a bigger batch must split it and send
 * the chunks one after another.
 *
 * The endpoint accepts up to 5,000 rows, but accepting them is not the
 * constraint — it applies each row in its own sequential transaction, so the
 * whole SOFTSUPP-532 batch (~3,750 rows) in one call would run for the better
 * part of staff-bff's 120 s ceiling. A timeout there does not merely fail the
 * run: the pre-update state needed to roll it back is returned in the response
 * and stored nowhere else, so it would be lost along with the connection.
 *
 * 500 puts that batch in eight requests of a few seconds each, and caps what a
 * single failure can cost to the one chunk that was in flight.
 */
export const APPLY_CHUNK_SIZE = 500;

export type BulkWebsiteRelabelRow = {
  table: RelabelTable;
  id: string;
  currentWebsite: RelabelWebsite;
  currentLanguageCode: string | null;
  targetWebsite: RelabelWebsite;
  targetLanguageCode: string | null;
};

export type ValidateStatus =
  | "ok"
  | "not_found"
  | "stale_current"
  | "constraint_violation"
  | "group_collision"
  | "noop";

/**
 * A glimpse of the profile behind a row, so an anomaly can be judged without
 * leaving the page — a row flagged `content_looks_en` is only a guess until
 * someone reads the text.
 *
 * `excerpt` is profile content, i.e. untrusted: render it as plain text.
 */
export type RelabelPreview = {
  /** Which field the excerpt was taken from, e.g. "profile_overview". */
  label: string;
  excerpt: string;
  /** The profile's display name, when the API could resolve one. */
  name?: string;
};

/**
 * One result per submitted row, in the SAME ORDER as the request. Merge by
 * index rather than by id — an uploaded file may contain duplicate ids, and
 * only position ties a result back to its row unambiguously.
 */
export type ValidateRowResult = {
  id: string;
  table: RelabelTable;
  status: ValidateStatus;
  detail?: string;
  /**
   * Optional, and treated as optional throughout the UI: the page predates it
   * and must keep working against an API that does not send it.
   */
  preview?: RelabelPreview;
};

export type ValidateSummary = Record<ValidateStatus, number>;

export type BulkWebsiteRelabelValidateResponse = {
  results: ValidateRowResult[];
  summary: ValidateSummary;
};

export type ApplyStatus = ValidateStatus | "failed";

export type ApplyRowResult = {
  id: string;
  table: RelabelTable;
  status: ApplyStatus;
  detail?: string;
  /**
   * Pre-update DB state, present ONLY on rows actually applied (status "ok").
   * This is what the rollback manifest is built from.
   */
  previous?: {
    website: RelabelWebsite;
    languageCode: string | null;
  };
};

export type ApplySummary = Record<ApplyStatus, number>;

export type BulkWebsiteRelabelApplyResponse = {
  results: ApplyRowResult[];
  summary: ApplySummary;
};

export const websiteRelabelService = {
  async validate(rows: BulkWebsiteRelabelRow[]): Promise<BulkWebsiteRelabelValidateResponse> {
    return api.post<BulkWebsiteRelabelValidateResponse>(`${BASE}/validate`, { rows });
  },

  async apply(rows: BulkWebsiteRelabelRow[]): Promise<BulkWebsiteRelabelApplyResponse> {
    return api.post<BulkWebsiteRelabelApplyResponse>(`${BASE}/apply`, { rows });
  },
};
